import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import { MAX_TITLE_LENGTH, MIN_TITLE_LENGTH } from '@opengov/stories-shared/constants';
import { IPublicationNavMenuStory } from '@opengov/stories-shared/types';
import React, { useEffect, useMemo, useState } from 'react';

import { IOnlinePublicationMessages } from '../../../../../../types';
import { useSetupOnlinePublicationUi } from '../../context/SetupOnlinePublicationUi.context';
import { useUpdateMenuStoryName } from '../../hooks/useUpdateMenuStoryName';

import { styles } from './ChangeDisplayNameModal.styles';

export type IChangeDisplayNameModalProps = {
  messages: IOnlinePublicationMessages;
  selectedMenuStory: IPublicationNavMenuStory;
  menuStories: IPublicationNavMenuStory[];
  onClose(): void;
};

export const ChangeDisplayNameModal: React.FC<IChangeDisplayNameModalProps> = (props) => {
  const { menuStories, messages, selectedMenuStory, onClose } = props;
  const changeDisplayNameDesc = messages.changeDisplayNameDesc as string | undefined;

  const [storyDisplayName, setStoryDisplayName] = useState(selectedMenuStory?.displayName || '');
  const [hasSameName, setHasSameName] = useState(false);
  const trimmedStoryDisplayName = useMemo(() => storyDisplayName?.trim() || '', [storyDisplayName]);

  const updateMenuStoryName = useUpdateMenuStoryName(
    { menuId: selectedMenuStory.menuId },
    {
      successTitle: messages.displayNameUpdated,
      successBody: messages.displayNameUpdatedDesc,
      errorTitle: messages.somethingWentWrongError,
      errorBody: messages.errorDescription
    }
  );
  const { dispatch: uiDispatch } = useSetupOnlinePublicationUi();

  useEffect(() => {
    if (menuStories) {
      const hasMatch = !!menuStories.find(
        (story) =>
          selectedMenuStory.id !== story.id && story.displayName && story.displayName === trimmedStoryDisplayName
      );
      setHasSameName(hasMatch);
    }
  }, [trimmedStoryDisplayName]);

  const saveButtonAction = () => {
    void updateMenuStoryName
      .mutateAsync({ id: selectedMenuStory.id, displayName: trimmedStoryDisplayName })
      .then(() => {
        uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true });
        onClose();
      })
      .catch(() => undefined);
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth data-test="change-display-name-modal" aria-labelledby="change-display-name-dialog-title">
      <DialogTitle id="change-display-name-dialog-title" sx={styles.dialogTitle}>
        {messages.changeDisplayName}
        <IconButton aria-label="Close" onClick={onClose} sx={styles.closeButton}>
          <CloseIcon aria-hidden={true} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {changeDisplayNameDesc && (
          <Typography variant="body2" color="text.secondary">
            {changeDisplayNameDesc}
          </Typography>
        )}
        <Box sx={styles.originalName}>
          <Typography variant="body2">{messages.originalStoryName}</Typography>
          <Typography variant="body2" sx={styles.storiesTextMedium}>
            {selectedMenuStory.story.title}
          </Typography>
        </Box>
        <Box sx={styles.labelContainer}>
          <Typography variant="caption" color="text.secondary">
            {`${trimmedStoryDisplayName.length}/${MAX_TITLE_LENGTH}`}
          </Typography>
        </Box>
        <TextField
          fullWidth
          label={messages.displayName}
          value={storyDisplayName}
          onChange={(e) => setStoryDisplayName(e.target.value)}
          placeholder={messages.enterName}
          inputProps={{
            maxLength: MAX_TITLE_LENGTH,
            'aria-describedby': hasSameName ? 'change-display-name-error' : undefined,
            'data-test': 'add-menu-modal-name-input',
            className: 'no-format'
          }}
        />
        {hasSameName && (
          <Typography id="change-display-name-error" variant="caption" color="error">
            {messages.noSameStoryName}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          data-test="change-display-name-modal-cancel-button"
          onClick={onClose}
        >
          {messages.cancel}
        </Button>
        <LoadingButton
          loading={updateMenuStoryName.isLoading}
          variant="contained"
          size="small"
          data-test="change-display-name-modal-add-button"
          disabled={
            (trimmedStoryDisplayName.length < MIN_TITLE_LENGTH && trimmedStoryDisplayName.length !== 0) ||
            trimmedStoryDisplayName.length > MAX_TITLE_LENGTH ||
            hasSameName ||
            selectedMenuStory.displayName === trimmedStoryDisplayName
          }
          onClick={saveButtonAction}
        >
          {messages.save}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
